'use client';

import Link from 'next/link';
import { ChevronRight } from 'lucide-react';

import type { PostEvent } from '@kit/community';
import { cdnImageUrl } from '@kit/shared/storage';
import { cn } from '@kit/ui/cn';
import { hashSeededGradient } from '@kit/ui/entity-card';

import { auditionEmbedVariant } from './post-attachments.audition-cards';
import {
  EventDeadline,
  eventHref,
  EventMeta,
  EventTitle,
  EventTypeBadge,
  EventTypeLabel,
} from './post-attachments.event-parts';

/**
 * Hero embed — the post IS the event, so the cover IS the card: one
 * full-bleed 16:9 visual with everything overlaid. Top-left: floating
 * identity chip. Top-right: deadline pill. Bottom gradient: type badge +
 * title. The whole cover links to `/events/<slug>` (only when the event has
 * a slug); coverless events get a hash-seeded gradient.
 */
function EventHeroCard({
  event,
  identity,
}: {
  event: PostEvent;
  identity?: React.ReactNode;
}) {
  const href = eventHref(event);
  const initials = (event.title ?? 'E').slice(0, 2).toUpperCase();
  const linkable = href !== '#';

  const cover = (
    <div
      className="relative aspect-[16/9] w-full overflow-hidden"
      style={
        event.coverUrl
          ? undefined
          : { background: hashSeededGradient(event.id) }
      }
    >
      {event.coverUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={cdnImageUrl(event.coverUrl, 'GALLERY') ?? undefined}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
      ) : (
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center text-4xl font-semibold text-white/25"
        >
          {initials}
        </span>
      )}
      {/* Bottom gradient carrying badge + title + the key facts (when / where) */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent p-4 pt-12">
        <EventTypeBadge event={event} />
        <h3 className="mt-1.5 line-clamp-2 text-lg leading-snug font-semibold text-white">
          <EventTitle event={event} />
        </h3>
        <EventMeta event={event} className="mt-1.5" />
      </div>
    </div>
  );

  return (
    // No own rounding — same full-bleed hero rules as AuditionHeroCard.
    <div className="relative overflow-hidden">
      {identity}
      <EventDeadline
        event={event}
        variant="overlay"
        className="absolute top-3 right-3 z-10"
      />
      {linkable ? (
        <Link href={href} className="group block">
          {cover}
        </Link>
      ) : (
        <div className="group block">{cover}</div>
      )}
    </div>
  );
}

/**
 * Chip embed — the community event referenced from a longer text post.
 * Compact horizontal card: cover thumbnail (or gradient), kicker, title,
 * deadline. Links to `/events/<slug>` when the event has a slug.
 */
function EventChipCard({ event }: { event: PostEvent }) {
  const href = eventHref(event);
  const initials = (event.title ?? 'E').slice(0, 1).toUpperCase();
  const linkable = href !== '#';

  const inner = (
    <>
      <div
        className="relative h-[72px] w-[96px] shrink-0 overflow-hidden rounded-lg"
        style={
          event.coverUrl
            ? undefined
            : { background: hashSeededGradient(event.id) }
        }
      >
        {event.coverUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={cdnImageUrl(event.coverUrl, 'THUMBNAIL') ?? undefined}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center text-xl font-semibold text-white/30"
          >
            {initials}
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-muted-foreground text-xs tracking-wide uppercase">
          <EventTypeLabel eventType={event.eventType} />
        </span>
        <span className="text-foreground line-clamp-1 font-medium group-hover:underline">
          <EventTitle event={event} />
        </span>
        <EventDeadline event={event} />
      </div>

      <ChevronRight
        className="text-muted-foreground h-4 w-4 shrink-0"
        aria-hidden="true"
      />
    </>
  );

  const cardClass =
    'border-border group flex items-center gap-3 overflow-hidden rounded-lg border p-3';

  return linkable ? (
    <Link
      href={href}
      className={cn(cardClass, 'hover:bg-muted/50 transition-colors')}
    >
      {inner}
    </Link>
  ) : (
    <div className={cardClass}>{inner}</div>
  );
}

export function EventEmbed({
  event,
  variant,
  identity,
}: {
  event: PostEvent;
  variant: 'hero' | 'chip';
  // Floating top-left identity chip (post author) — hero only.
  identity?: React.ReactNode;
}) {
  return variant === 'hero' ? (
    <EventHeroCard event={event} identity={identity} />
  ) : (
    <EventChipCard event={event} />
  );
}

/**
 * Same short-body-is-a-lead-in heuristic as `auditionEmbedVariant`: a short
 * body renders the event as a hero, a longer one as a compact chip inside
 * prose.
 */
export function eventEmbedVariant(body: string): 'hero' | 'chip' {
  return auditionEmbedVariant(body);
}
